import { KeyboardEvent, SyntheticEvent, useState } from 'react';
import { ScholarshipCard as Scholarship, ScholarshipStatus } from '@common-types';
import { FormControl, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material';

import { CoreBox, CoreButton, CoreChip, CoreTooltip, CoreTypography } from '@youscience/core';

import {
  AWARD_TYPE_ICONS,
  getMonthName,
  moneyFormatter,
  SMALL_DESC_LENGTH,
  WIDE_DESC_LENGTH,
  TAGS_COLORS,
} from './BaseScholarshipCard.constants';

import FlagIcon from '@images/flag.svg?react';
import DollarIcon from '@images/scholarship-card/dollar.svg?react';
import HeartIcon from '@images/heart.svg?react';
import FilledHeartIcon from '@images/scholarship-card/heart-filled.svg?react';
import YesOptionIcon from '@images/yes-option.svg?react';
import NoOptionIcon from '@images/no-option.svg?react';
import SaveIcon from '@images/scholarship-card/mark.svg?react';
import EditIcon from '@images/scholarship-card/pencil.svg?react';
import CancelIcon from '@images/scholarship-card/cancel.svg?react';

import { LeavingSiteModal } from '@components/LeavingSiteModal';
import { UtahBadge } from '@components/UtahBadge';

import {
  sxStyles,
  StyledCardWrap,
  StyledTopRow,
  StyledProgramName,
  StyledActionsWrap,
  StyledApplyStepBtn,
  StyledActionIcon,
  StyledFavoriteButton,
} from './BaseScholarshipCard.styles';
import { useStateSpotlight } from '@hooks/useStateSpotlight';

export interface BaseScholarshipCardProps {
  scholarship: Scholarship;
  isWiderCardType?: boolean;
  isViewOnly?: boolean;
  onMoreDetails: () => void;
  initialApplyStatus: ScholarshipStatus;
  onFavoriteToggle: () => void;
  onApplyScholarship: (id: number) => void;
  onChangeApplyStatus: (id: number, status: ScholarshipStatus) => void;
}

export const BaseScholarshipCard = (props: BaseScholarshipCardProps) => {
  const {
    scholarship: {
      id,
      awardType,
      programName,
      donorName,
      description,
      moneyRange,
      applicationDate,
      isFavorite = false,
      webAddress,
      utahBadge: rawUtahBadge,
    },
    isWiderCardType = false,
    isViewOnly = false,
    onMoreDetails,
    initialApplyStatus = ScholarshipStatus.CHANGED,
    onFavoriteToggle,
    onApplyScholarship,
    onChangeApplyStatus,
  } = props;

  const { spotlight } = useStateSpotlight();
  const utahBadge = spotlight ? rawUtahBadge : undefined;

  const [isApplyStepActive, setIsApplyStepActive] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentApplyStatus, setCurrentApplyStatus] = useState<ScholarshipStatus>(initialApplyStatus);
  const [isOpenLeavingSiteModal, setIsOpenLeavingSiteModal] = useState(false);
  const [previousInitialApplyStatus, setPreviousInitialApplyStatus] = useState<ScholarshipStatus>(initialApplyStatus);

  if (initialApplyStatus !== previousInitialApplyStatus) {
    setPreviousInitialApplyStatus(initialApplyStatus);
    setCurrentApplyStatus(initialApplyStatus);
  }

  const deadline = `Deadline: ${getMonthName(applicationDate.deadlineMonth as number)} ${
    applicationDate.deadlineDay
  }, ${applicationDate.deadlineYear}`;

  const fullMoneyRange =
    moneyRange?.lowerLimit && moneyRange?.upperLimit
      ? `${moneyFormatter.format(moneyRange?.lowerLimit)} – ${moneyFormatter.format(moneyRange?.upperLimit)}`
      : moneyFormatter.format(moneyRange?.lowerLimit as number);

  const maxDescriptionLength = isWiderCardType ? WIDE_DESC_LENGTH : SMALL_DESC_LENGTH;

  const handleToggleFavorite = (event: SyntheticEvent) => {
    event.stopPropagation();
    onFavoriteToggle();
  };

  const handleOnApplyStep = () => {
    setIsOpenLeavingSiteModal(true);

    setIsApplyStepActive(true);
  };

  const handleOffApplyStep = () => {
    setIsApplyStepActive(false);
  };

  const handleOnEditMode = () => {
    setIsEditMode(true);
  };

  const handleOffEditMode = () => {
    setCurrentApplyStatus(initialApplyStatus || ScholarshipStatus.CHANGED);
    setIsEditMode(false);
  };

  const handleApplyScholarship = () => {
    setCurrentApplyStatus(ScholarshipStatus.IN_PROGRESS);

    void onApplyScholarship(id);

    setIsApplyStepActive(false);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    setCurrentApplyStatus(event.target.value as ScholarshipStatus);
  };

  const handleSaveApplyStatus = () => {
    if (initialApplyStatus !== currentApplyStatus) {
      void onChangeApplyStatus(id, currentApplyStatus);
    }

    setIsEditMode(false);
  };

  const handleStopPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const handleDetailsKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onMoreDetails();
    }
  };

  const handleOnCancelLeavingSiteModal = () => {
    setIsOpenLeavingSiteModal(false);
  };

  const handleOnContinueLeavingSiteModal = () => {
    window.open(webAddress.programUrl, '_blank', 'noopener,noreferrer');

    setIsOpenLeavingSiteModal(false);
  };

  return (
    <>
      <StyledCardWrap data-testid={`scholarshipCard_${id}`} isWiderCardType={isWiderCardType} onClick={onMoreDetails}>
        <CoreBox sx={sxStyles.cardContentWrap}>
          <StyledTopRow>
            <CoreBox data-testid="awardType" sx={sxStyles.awardType}>
              {AWARD_TYPE_ICONS[awardType]}

              <CoreTypography variant="overline" component="h6">
                {awardType}
              </CoreTypography>
            </CoreBox>

            {utahBadge && <UtahBadge variant={utahBadge} />}
          </StyledTopRow>

          <CoreTooltip title={programName}>
            <StyledProgramName data-testid="programName" variant="h6" component="h5">
              <span role="link" tabIndex={0} onKeyDown={handleDetailsKeyDown}>
                {programName}
              </span>
            </StyledProgramName>
          </CoreTooltip>

          <CoreTypography data-testid="donorName" sx={sxStyles.donorName} variant="caption">
            {`Offered by: ${donorName}`}
          </CoreTypography>

          <CoreTypography data-testid="description" sx={sxStyles.description}>
            {`${description.slice(0, maxDescriptionLength)}... `}

            <CoreTypography sx={sxStyles.learnMore} component="span">
              learn more
            </CoreTypography>
          </CoreTypography>

          <CoreBox sx={sxStyles.additionalInfo}>
            {!!applicationDate.deadlineMonth && !!applicationDate.deadlineDay && !!applicationDate.deadlineYear && (
              <CoreBox sx={sxStyles.additionalInfoItem}>
                <FlagIcon data-testid="deadlineIcon" />

                <CoreTypography data-testid="deadline" variant="caption">
                  {deadline}
                </CoreTypography>
              </CoreBox>
            )}

            {moneyRange?.lowerLimit ? (
              <CoreBox sx={sxStyles.additionalInfoItem}>
                <DollarIcon data-testid="awardIcon" />

                <CoreTypography data-testid="award" variant="caption">{`Award: ${fullMoneyRange}`}</CoreTypography>
              </CoreBox>
            ) : null}
          </CoreBox>
        </CoreBox>

        <StyledActionsWrap onClick={handleStopPropagation}>
          {isApplyStepActive ? (
            <CoreBox sx={sxStyles.applyStepWrap}>
              <CoreBox sx={sxStyles.applyQuestion}>
                <CoreTypography>Did you start your application?</CoreTypography>
              </CoreBox>

              <CoreBox sx={sxStyles.alignCenterWrap}>
                <StyledApplyStepBtn onClick={handleApplyScholarship}>
                  <YesOptionIcon />

                  <CoreTypography>Yes</CoreTypography>
                </StyledApplyStepBtn>

                <StyledApplyStepBtn onClick={handleOffApplyStep}>
                  <NoOptionIcon />

                  <CoreTypography>No</CoreTypography>
                </StyledApplyStepBtn>
              </CoreBox>
            </CoreBox>
          ) : (
            <>
              {initialApplyStatus !== ScholarshipStatus.CHANGED ? (
                <CoreBox sx={sxStyles.editModeWrap}>
                  {isEditMode ? (
                    <CoreBox sx={sxStyles.alignCenterWrap}>
                      <FormControl size="small" sx={sxStyles.selectWrap}>
                        <InputLabel id={`applyStatusLabel_${id}`}>Application status</InputLabel>

                        <Select
                          data-testid="applyStepSelect"
                          labelId={`applyStatusLabel_${id}`}
                          value={currentApplyStatus}
                          onChange={handleSelectChange}
                          input={<OutlinedInput label="Application status" />}
                          renderValue={(selected) => (
                            <CoreChip
                              key={selected}
                              sx={{
                                color: TAGS_COLORS[currentApplyStatus],
                              }}
                              label={selected}
                              variant="outlined"
                            />
                          )}
                        >
                          {Object.keys(ScholarshipStatus).map((key) => (
                            <MenuItem key={key} value={ScholarshipStatus[key as keyof typeof ScholarshipStatus]}>
                              {(ScholarshipStatus as Record<string, unknown>)[key] as ScholarshipStatus}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <StyledActionIcon
                        type="button"
                        aria-label="Save application status"
                        onClick={handleSaveApplyStatus}
                      >
                        <SaveIcon />
                      </StyledActionIcon>

                      <StyledActionIcon type="button" aria-label="Cancel editing" onClick={handleOffEditMode}>
                        <CancelIcon />
                      </StyledActionIcon>
                    </CoreBox>
                  ) : (
                    <CoreBox sx={sxStyles.alignCenterWrap}>
                      <CoreChip
                        data-testid="applyStatusChip"
                        sx={{ color: TAGS_COLORS[currentApplyStatus] }}
                        label={currentApplyStatus}
                        variant="outlined"
                        size="medium"
                      />
                      {isViewOnly ? null : (
                        <StyledActionIcon
                          type="button"
                          aria-label="Edit application status"
                          onClick={handleOnEditMode}
                          data-testid="scholarshipEdit"
                        >
                          <EditIcon />
                        </StyledActionIcon>
                      )}
                    </CoreBox>
                  )}
                  <StyledFavoriteButton
                    type="button"
                    aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    data-testid="scholarshipFavorite"
                    onClick={handleToggleFavorite}
                  >
                    {isFavorite ? <FilledHeartIcon /> : <HeartIcon />}
                  </StyledFavoriteButton>
                </CoreBox>
              ) : (
                <>
                  <StyledFavoriteButton
                    type="button"
                    aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    data-testid="scholarshipFavorite"
                    onClick={handleToggleFavorite}
                  >
                    {isFavorite ? <FilledHeartIcon /> : <HeartIcon />}
                  </StyledFavoriteButton>

                  <CoreButton data-testid="scholarshipApply" sx={sxStyles.applyBtn} onClick={handleOnApplyStep}>
                    Apply
                  </CoreButton>
                </>
              )}
            </>
          )}
        </StyledActionsWrap>
      </StyledCardWrap>

      {isOpenLeavingSiteModal && (
        <LeavingSiteModal
          isOpenLeavingSiteModal={isOpenLeavingSiteModal}
          website={webAddress.sponsorUrl}
          onCancel={handleOnCancelLeavingSiteModal}
          onContinue={handleOnContinueLeavingSiteModal}
        />
      )}
    </>
  );
};
